import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { Table } from '@/lib/components';
import { CheckConfirmCircleIcon, QuestionCircleIcon, WarningCircleIcon } from '@/lib/icon';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import { Option } from '@/lib/v2/components/Select/Select';

import { isValidFormat } from '@/src/application/util/validators';

import { countryOptions, dateOptions, SelectorEnum, sexOptions } from './selectorsValues';
import SelectRow from './SelectRow';

import { StateOption } from '@/modules/ContactsModule/screens/ImportMain/StateOptionsEnum.enum';
import {
  ConfigColumProps,
  CustomsFieldsProps,
} from '@/modules/ContactsModule/screens/ImportMain/V2ImportTypes';

export interface ITableMappingField {
  idColumn: number;
  header: string;
  data: Array<string>;
  mappingField: Option;
  formatColumn?: {
    dataType: string;
    selectors?: Option[];
    selected?: Option | null;
  };
}

export interface IMappingField {
  mappingList: { mapping: ITableMappingField }[];
}

export const noNameSelect = {
  id: StateOption.noImport,
  name: t('IMPORT_MAIN.noName'),
  value: t('IMPORT_MAIN.noName'),
} as Option;

export const noImport = {
  id: StateOption.noImport,
  name: t('IMPORT_MAIN.noImport'),
  value: t('IMPORT_MAIN.noImport'),
};

const matchValue = (customsFields: CustomsFieldsProps[], header: string) => {
  const matches = customsFields.find((element: CustomsFieldsProps) => {
    return element.name === header;
  });

  return matches
    ? ({ id: matches.id, name: matches.name, value: matches.value } as Option)
    : noNameSelect;
};

export const matchValueSelector = (dataType: string): Option[] | undefined => {
  let selector: Option[] | undefined = undefined;

  if (dataType === SelectorEnum.date) {
    selector = dateOptions;
  } else if (dataType === SelectorEnum.sex) {
    selector = sexOptions;
  } else if (dataType === SelectorEnum.country) {
    selector = countryOptions;
  }

  return selector;
};

export const wrappingMappingData = (
  customsFields: Array<CustomsFieldsProps>,
  configColumn: ConfigColumProps
) => {
  const mappingField = {
    mappingList: [],
  } as IMappingField;

  for (let i = 0; i < configColumn.columns.length; i++) {
    const header = customsFields.find(
      (element) =>
        element.id === configColumn.columns[i].idCustomField.toString() ||
        configColumn.columns[i].idCustomField === 0
    );
    if (header !== undefined) {
      const dataTypeSelectors = matchValueSelector(header.dataType);
      mappingField.mappingList.push(
        configColumn.columns[i].idCustomField !== 0
          ? {
              mapping: {
                idColumn: i,
                header: header.name,
                data: configColumn.columns[i].value.slice(0, 3),
                mappingField: matchValue(customsFields, header.name),
                formatColumn: {
                  dataType: header.dataType,
                  selectors: dataTypeSelectors,
                  selected: dataTypeSelectors?.at(0),
                },
              },
            }
          : {
              mapping: {
                idColumn: i,
                header: 'no name',
                data: configColumn.columns[i].value.slice(0, 3),
                mappingField: {
                  id: StateOption.noImport,
                  name: 'no name',
                  value: 'no name',
                } as Option,
                formatColumn: {
                  dataType: header.dataType,
                  selectors: undefined,
                  selected: null,
                },
              },
            }
      );
    }
  }

  return mappingField;
};

export type ContactsMappingFieldProps = {
  mappingList: IMappingField;
  setMappingList: (option: Option, id: string | number, idColumn: number) => void;
  listCustomField: Option[];
  onchangeDataType: (option: Option, id: string | number) => void;
};

const WarningDataMatch = ({
  data,
  format,
  dataType,
  id,
}: {
  data: string[];
  format: string;
  dataType: string;
  id: string | number;
}) => {
  let warningIcon = false;

  if (dataType === SelectorEnum.date || dataType === SelectorEnum.sex) {
    warningIcon = data.some((element: string) => !isValidFormat(element, format, dataType));
  }

  if (id === StateOption.noImport) {
    return <QuestionCircleIcon />;
  } else if (warningIcon) {
    return <WarningCircleIcon />;
  } else {
    return <CheckConfirmCircleIcon green />;
  }
};

export const ContactsMappingField = ({
  mappingList,
  setMappingList,
  listCustomField,
  onchangeDataType,
}: ContactsMappingFieldProps) => {
  const { t } = useTranslation();
  const headerColum = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="text-[#004DBC]">{t('IMPORT_MAIN.header')}</span>
      </Flex>
    ),
    accessor: 'mapping',
    id: 'mapping.header',
    Cell: ({ cell: { value } }: { cell: { value: ITableMappingField } }) => {
      const { id } = value.mappingField;
      const format = value.formatColumn?.selected?.id.toString();
      const dataType = value.formatColumn?.dataType;

      return (
        <Flex key={`headerColumn-${value.idColumn}`} withGap alignment="start">
          <Flex noGrow withGap gapSize="medium">
            <div className="h-6 w-6">
              {value.mappingField.id === StateOption.noImport ? (
                <QuestionCircleIcon />
              ) : (
                <WarningDataMatch
                  data={value.data}
                  dataType={dataType ?? ''}
                  format={format ?? ''}
                  id={id}
                />
              )}
            </div>
            <span className="self-start font-medium uppercase text-[#364365]">{value.header}</span>
          </Flex>
        </Flex>
      );
    },
  };

  const dataColum = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="text-[#004DBC]">{t('IMPORT_MAIN.data')}</span>
      </Flex>
    ),
    accessor: 'mapping',
    id: 'data',
    Cell: ({ cell: { value } }: { cell: { value: ITableMappingField } }) => {
      const format = value.formatColumn?.selected?.id.toString();
      const dataType = value.formatColumn?.dataType;

      return (
        <Flex key={`dataColumn-${value.idColumn}`} withGap alignment="start">
          <Flex column noGrow>
            <span className="self-start text-12 font-medium text-[#364365]">
              <Flex column itemAlignment="start">
                {value.data.map((element) => {
                  return (
                    <div
                      key={_.uniqueId()}
                      className={`truncate md:max-w-[400px] 2xl:max-w-[800px] ${
                        format &&
                        value.mappingField.id !== StateOption.noImport &&
                        !isValidFormat(element, format, dataType) &&
                        'text-rose-600'
                      }
                    `}
                    >
                      {element}
                    </div>
                  );
                })}
              </Flex>
            </span>
          </Flex>
        </Flex>
      );
    },
  };

  const mappingFieldColum = {
    Header: () => (
      <Flex withGap alignment="start">
        <span className="text-[#004DBC]">{t('IMPORT_MAIN.fieldMapping')}</span>
      </Flex>
    ),
    accessor: 'mapping',
    id: 'mapping.mappingField',
    Cell: ({ cell: { value } }: { cell: { value: ITableMappingField } }) => {
      const { id } = value.mappingField;
      const format = value.formatColumn?.selected?.name as string;
      const dataType = value.formatColumn?.dataType;
      let isSomeInvalid = false;

      if (dataType && format) {
        isSomeInvalid = value.data.some(
          (element: string) => !isValidFormat(element, format, dataType)
        );
      }

      return (
        <SelectRow
          key={`mappingFieldColumn-${value.idColumn}`}
          id={id}
          isInvalid={isSomeInvalid}
          listCustomField={listCustomField}
          onchangeDataType={onchangeDataType}
          option={value.mappingField}
          selectDataType={value.formatColumn?.selectors}
          selectIndex={value.idColumn}
          selected={value.formatColumn?.selected as Option}
          value={value}
          onChange={setMappingList}
        />
      );
    },
  };

  const mappingFieldsColumns: Column<object>[] = [headerColum, dataColum, mappingFieldColum].filter(
    (x) => x
  ) as Column<object>[];

  return (
    <div className="w-full text-base text-[#364365] 2xl:text-14">
      <Table columns={mappingFieldsColumns} data={mappingList.mappingList} overflow={'visible'} />
    </div>
  );
};
